import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _class, _CategorizationEdit;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
/** @jsx jsx */
import { Component } from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/fp/find';
import flatMap from 'lodash/fp/flatMap';
import isEqual from 'lodash/fp/isEqual';
import flow from 'lodash/fp/flow';
import filter from 'lodash/fp/filter';
import sortBy from 'lodash/fp/sortBy';
import set from 'lodash/fp/set';
import { v4 as uuid } from 'uuid';
import striptags from 'striptags';
import { Text } from '@instructure/ui-text';
import { jsx } from '@instructure/emotion';
import CategorizationInteractionType from '../../../records/interactions/categorization';
import ChoiceInput from '../../common/edit/components/ChoiceInput';
import Footer from '../../common/edit/components/Footer';
import QuestionSettingsContainer from '../../common/edit/components/QuestionSettingsContainer';
import QuestionContainer from '../../common/edit/components/QuestionContainer';
import { getNextItemIdFromArray } from '../../../util/focusHelpers';
import CategorizationPresenter from './CategorizationPresenter';
import CategoryForm from './CategoryForm';
import { getSortedCategories } from '../common/utils';
import withEditTools from '../../../util/withEditTools';
import t from '@instructure/quiz-i18n/es/format-message';
import QuestionSettingsPanel from '../../common/edit/components/QuestionSettingsPanel';
import CalculatorOptionWithOqaatAlert from '../../common/edit/components/CalculatorOptionWithOqaatAlert';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { FormFieldGroup, Flex } from '@instructure/quiz-common';

/**
---
category: Categorization
---
  Categorization Edit component

  ```jsx_example
  function Example (props) {
    const exampleProps = {
      itemBody: 'Match the name of the celestial body on the left with its correct classification:',
      interactionData: {
        categoryOrder: ['uuid1', 'uuid2'],
        categories: {
          uuid1: { id: 'uuid1', itemBody: 'Planet' },
          uuid2: { id: 'uuid2', itemBody: 'Moon' }
        },
        distractors: {
          uuid3: { id: 'uuid3', itemBody: 'Mars' },
          uuid4: { id: 'uuid4', itemBody: 'Europa' },
          uuid5: { id: 'uuid5', itemBody: 'Venus' },
          uuid6: { id: 'uuid6', itemBody: 'Phobos' },
          uuid7: { id: 'uuid7', itemBody: 'Deimos' },
          uuid8: { id: 'uuid8', itemBody: 'Jupiter' },
          uuid9: { id: 'uuid9', itemBody: 'America' },
          uuid10: { id: 'uuid10', itemBody: 'Asia' }
        }
      },
      scoringData: {
        value: [{
          id: 'uuid1',
          scoringAlgorithm: 'AllOrNothing',
          scoringData: {
            value: ['uuid3', 'uuid5', 'uuid8']
          }
        }, {
          id: 'uuid2',
          scoringAlgorithm: 'AllOrNothing',
          scoringData: {
            value: ['uuid4', 'uuid6', 'uuid7']
          }
        }]
      }
    }

    return (
      <CategorizationEdit {...exampleProps} {...props} />
    )
  }

  <SettingsSwitcher locales={LOCALES}>
    <EditStateProvider>
      <Example />
    </EditStateProvider>
  </SettingsSwitcher>
  ```
**/
var CategorizationEdit = withEditTools(_class = (_CategorizationEdit = /*#__PURE__*/function (_Component) {
  function CategorizationEdit(props) {
    var _this;
    _classCallCheck(this, CategorizationEdit);
    _this = _callSuper(this, CategorizationEdit, [props]);
    _defineProperty(_this, "distractorRefs", {});
    _defineProperty(_this, "categoryRef", null);
    _defineProperty(_this, "stemElement", null);
    // ===========
    //   HELPERS
    // ===========
    _defineProperty(_this, "getViewDistractors", function () {
      var answerIds = flatMap(function (_ref) {
        var value = _ref.scoringData.value;
        return value;
      }, _this.props.scoringData.value);
      return flow(Object.values, filter(function (_ref2) {
        var id = _ref2.id;
        return !answerIds.includes(id);
      }), sortBy(function (_ref3) {
        var id = _ref3.id;
        return _this.state.distractorOrder.indexOf(id);
      }))(_this.props.interactionData.distractors);
    });
    _defineProperty(_this, "getCategoryErrors", function (categoryId) {
      return _this.props.getErrors("interactionData.categories.".concat(categoryId, ".itemBody"));
    });
    _defineProperty(_this, "getDistractorErrors", function (distractorId) {
      return _this.props.getErrors("interactionData.distractors.".concat(distractorId, ".itemBody"));
    });
    _defineProperty(_this, "getAnswerIds", function (categoryId) {
      var categoryScoringData = find({
        id: categoryId
      }, _this.props.scoringData.value);

      // since kinesis doesnt save empty array
      return categoryScoringData.scoringData.value || [];
    });
    // ===========
    //   HANDLERS
    // ===========
    _defineProperty(_this, "handleCalculatorTypeChange", function (e, value) {
      _this.props.changeItemState({
        calculatorType: value
      });
    });
    _defineProperty(_this, "handleRemoveDistractor", function (distractorId) {
      var nextDistractorId = getNextItemIdFromArray(distractorId, _this.getViewDistractors());
      var nextDistractorRef = _this.distractorRefs[nextDistractorId];
      if (nextDistractorRef) {
        nextDistractorRef.removeChoicebutton.focus();
      } else {
        // focus on add category button
        _this.categoryRef.footerRef.focus();
      }
      _this.presenter.onRemoveDistractor(distractorId);
    });
    _defineProperty(_this, "handleRemoveCategory", function (categoryId) {
      var categoryOrder = _this.props.interactionData.categoryOrder;
      var removingFirstCategory = categoryOrder && categoryOrder.indexOf(categoryId) === 0;
      if (removingFirstCategory) {
        _this.stemElement.focus();
      } else {
        var nextCategoryId = getNextItemIdFromArray(categoryId, _this.state.sortedCategories);
        var nextAddAnswerRef = _this.categoryRef.categoryRefs[nextCategoryId];
        if (nextAddAnswerRef) {
          // will focus the previous category's "+Answer" button
          nextAddAnswerRef.focus();
        }
      }
      _this.presenter.onRemoveCategory(categoryId);
    });
    _defineProperty(_this, "handleRemoveAnswer", function (answerId, categoryId) {
      var answerIds = _this.getAnswerIds(categoryId);
      var removingFirstAnswer = answerIds.indexOf(answerId) === 0;
      if (removingFirstAnswer) {
        _this.categoryRef.categoryAnswerInputRefs[categoryId].answerInput.focus();
      } else {
        var nextAnswerId = getNextItemIdFromArray(answerId, answerIds);
        var nextAnswerRef = _this.categoryRef.answerRefs[nextAnswerId];
        if (nextAnswerRef) {
          nextAnswerRef.removeChoicebutton.focus();
        }
      }
      _this.presenter.onRemoveAnswer(answerId, categoryId);
    });
    _defineProperty(_this, "handleCreateDistractor", function () {
      var id = _this.props.newId();
      var newDistractor = {
        id: id,
        itemBody: ''
      };
      _this.setState(function (prevState) {
        return {
          distractorOrder: prevState.distractorOrder.concat([id])
        };
      });
      _this.props.changeItemState({
        interactionData: set("distractors[".concat(id, "]"), newDistractor, _this.props.interactionData)
      });
    });
    _defineProperty(_this, "handleStemRef", function (node) {
      _this.stemElement = node;
    });
    _defineProperty(_this, "handleCategoryRef", function (node) {
      _this.categoryRef = node;
    });
    _defineProperty(_this, "handleDescriptionChange", function (itemBody) {
      _this.props.changeItemState({
        itemBody: itemBody
      });
    });
    _this.presenter = new CategorizationPresenter(props);
    var _this$props$interacti = _this.props.interactionData,
      categories = _this$props$interacti.categories,
      _categoryOrder = _this$props$interacti.categoryOrder;
    _this.state = {
      sortedCategories: getSortedCategories(categories, _categoryOrder),
      distractorOrder: sortBy('itemBody', props.interactionData.distractors).map(function (_ref4) {
        var id = _ref4.id;
        return id;
      })
    };
    return _this;
  }
  _inherits(CategorizationEdit, _Component);
  return _createClass(CategorizationEdit, [{
    key: "UNSAFE_componentWillReceiveProps",
    value: function UNSAFE_componentWillReceiveProps(nextProps) {
      this.presenter.UNSAFE_componentWillReceiveProps(nextProps);
      var _nextProps$interactio = nextProps.interactionData,
        categories = _nextProps$interactio.categories,
        categoryOrder = _nextProps$interactio.categoryOrder;
      if (!isEqual(this.props.interactionData.categories, categories) || !isEqual(this.props.interactionData.categoryOrder, categoryOrder)) {
        this.setState({
          sortedCategories: getSortedCategories(categories, categoryOrder)
        });
      }
    }
  }, {
    key: "renderDistractor",
    value:
    // ===========
    //   RENDER
    // ===========

    function renderDistractor(distractor, focusOnMount) {
      var _this2 = this;
      return jsx(ChoiceInput, {
        key: distractor.id,
        id: distractor.id,
        disabledFields: this.props.overrideEditableForRegrading ? ['answerInput'] : [],
        itemBody: distractor.itemBody,
        errors: this.getDistractorErrors(distractor.id),
        onInputChange: this.presenter.onDistractorInputChange,
        onModalClose: this.props.onModalClose,
        onModalOpen: this.props.onModalOpen,
        onRemoveChoice: function onRemoveChoice() {
          return _this2.handleRemoveDistractor(distractor.id);
        },
        ref: function ref(node) {
          _this2.distractorRefs[distractor.id] = node;
        },
        renderLabel: t('Distractor'),
        isRequired: true,
        focusOnMount: focusOnMount,
        shouldRenderRemoveChoice: !this.props.overrideEditableForRegrading,
        noRCE: true,
        notifyScreenreader: this.props.notifyScreenreader,
        screenReaderText: t('Additional Distractor {itemBody}', {
          itemBody: striptags(distractor.itemBody)
        })
      });
    }
  }, {
    key: "renderDistractors",
    value: function renderDistractors(distractors) {
      var _this3 = this;
      return distractors.map(function (distractor, i, source) {
        var focusOnMount = i + 1 === source.length && !distractor.itemBody;
        return jsx(Flex.Item, {
          key: "".concat(distractor.id, "-i"),
          overflowY: "visible"
        }, _this3.renderDistractor(distractor, focusOnMount));
      });
    }
  }, {
    key: "renderDistractorSection",
    value: function renderDistractorSection() {
      var _this4 = this;
      var distractors = this.getViewDistractors();
      return jsx(Flex, {
        direction: "column",
        gap: "small",
        className: "distractorsContainer",
        padding: "x-small 0 0 0"
      }, jsx(Text, {
        color: "primary"
      }, t('Additional Distractors')), jsx(Flex, {
        direction: "column",
        gap: "small"
      }, distractors.map(function (distractor, i, source) {
        var focusOnMount = i + 1 === source.length && !distractor.itemBody;
        return _this4.renderDistractor(distractor, focusOnMount);
      })), !this.props.overrideEditableForRegrading && jsx(Flex.Item, {
        overflowY: "visible"
      }, jsx(Footer, {
        onCreateChoice: this.handleCreateDistractor,
        buttonText: t('Distractor'),
        screenReaderText: t('Add Distractor'),
        notifyScreenreader: this.props.notifyScreenreader,
        automationData: "add-categorization-distractor-button"
      })));
    }
  }, {
    key: "renderOptionsDescription",
    value: function renderOptionsDescription() {
      return jsx(ScreenReaderContent, null, t('Categorization options'));
    }
  }, {
    key: "render",
    value: function render() {
      var _this5 = this;
      this.distractorRefs = {};
      return jsx("div", null, jsx(QuestionContainer, {
        disabled: this.props.overrideEditableForRegrading,
        enableRichContentEditor: this.props.enableRichContentEditor,
        itemBody: this.props.itemBody,
        onDescriptionChange: this.handleDescriptionChange,
        onModalClose: this.props.onModalClose,
        onModalOpen: this.props.onModalOpen,
        openImportModal: this.props.openImportModal,
        stemErrors: this.props.getErrors('itemBody'),
        textareaRef: this.handleStemRef
      }, jsx(CategoryForm, {
        categoriesErrors: this.props.getErrors('scoringData.value.$errors')
        // These error callbacks are passed in as new functions, to force
        // the component to re-render when errors change. Not ideal - we
        // should be passing the errors in directly
        ,
        getCategoryErrors: function getCategoryErrors(categoryId) {
          return _this5.getCategoryErrors(categoryId);
        },
        distractorErrors: function distractorErrors(distractorId) {
          return _this5.getDistractorErrors(distractorId);
        },
        categoryScoringData: this.getAnswerIds,
        distractors: this.props.interactionData.distractors,
        disabled: this.props.overrideEditableForRegrading,
        sortedCategories: this.state.sortedCategories,
        notifyScreenreader: this.props.notifyScreenreader,
        onCategoryInputChange: this.presenter.onCategoryInputChange,
        onCreateAnswer: this.presenter.onCreateAnswer,
        onCreateCategory: this.presenter.onCreateCategory,
        onDistractorInputChange: this.presenter.onDistractorInputChange,
        onModalClose: this.props.onModalClose,
        onModalOpen: this.props.onModalOpen,
        onRemoveAnswer: this.handleRemoveAnswer,
        onRemoveCategory: this.handleRemoveCategory,
        ref: this.handleCategoryRef
      }), this.renderDistractorSection()), jsx(QuestionSettingsContainer, {
        additionalOptions: this.props.additionalOptions
      }, jsx(QuestionSettingsPanel, {
        label: t('Options'),
        defaultExpanded: true
      }, jsx(FormFieldGroup, {
        rowSpacing: "small",
        description: this.renderOptionsDescription()
      }, jsx(CalculatorOptionWithOqaatAlert, {
        disabled: this.props.overrideEditableForRegrading,
        calculatorValue: this.props.calculatorType,
        onCalculatorTypeChange: this.handleCalculatorTypeChange,
        oqaatChecked: this.props.oneQuestionAtATime,
        onOqaatChange: this.props.setOneQuestionAtATime
      })))));
    }
  }]);
}(Component), _defineProperty(_CategorizationEdit, "displayName", 'CategorizationEdit'), _defineProperty(_CategorizationEdit, "componentId", "Quizzes".concat(_CategorizationEdit.displayName)), _defineProperty(_CategorizationEdit, "interactionType", CategorizationInteractionType), _defineProperty(_CategorizationEdit, "propTypes", _objectSpread(_objectSpread({
  additionalOptions: QuestionSettingsContainer.propTypes.additionalOptions,
  calculatorType: PropTypes.string,
  changeItemState: PropTypes.func,
  enableRichContentEditor: PropTypes.bool,
  errors: PropTypes.shape({
    itemBody: PropTypes.arrayOf(PropTypes.string),
    interactionData: PropTypes.shape({
      distractors: PropTypes.objectOf(PropTypes.shape({
        itemBody: PropTypes.arrayOf(PropTypes.string)
      })),
      categories: PropTypes.objectOf(PropTypes.shape({
        itemBody: PropTypes.arrayOf(PropTypes.string)
      }))
    }),
    scoringData: PropTypes.shape({
      value: PropTypes.shape({
        $errors: PropTypes.arrayOf(PropTypes.string)
      })
    })
  }),
  interactionData: PropTypes.shape({
    categoryOrder: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.objectOf(PropTypes.shape({
      id: PropTypes.string,
      itemBody: PropTypes.string
    })),
    distractors: PropTypes.objectOf(PropTypes.shape({
      id: PropTypes.string,
      itemBody: PropTypes.string
    }))
  }).isRequired,
  itemBody: PropTypes.string,
  notifyScreenreader: PropTypes.func.isRequired,
  onModalClose: PropTypes.func,
  onModalOpen: PropTypes.func,
  oneQuestionAtATime: PropTypes.bool,
  openImportModal: PropTypes.func.isRequired,
  overrideEditableForRegrading: PropTypes.bool,
  scoringData: PropTypes.shape({
    value: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string
    }))
  }).isRequired,
  setOneQuestionAtATime: PropTypes.func,
  newId: PropTypes.func
}, withEditTools.injectedProps), {}, {
  styles: PropTypes.object
})), _defineProperty(_CategorizationEdit, "defaultProps", {
  calculatorType: 'none',
  enableRichContentEditor: true,
  oneQuestionAtATime: false,
  overrideEditableForRegrading: false,
  newId: uuid,
  setOneQuestionAtATime: Function.prototype,
  additionalOptions: void 0,
  changeItemState: void 0,
  errors: void 0,
  itemBody: void 0,
  onModalClose: void 0,
  onModalOpen: void 0
}), _CategorizationEdit)) || _class;
export { CategorizationEdit as default };